import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Mail, CheckCircle, Clock, TrendingUp } from 'lucide-react'
import { apiRequest } from '../../../shared/api/base'
import { mockApi } from '../../../mocks'

interface ValueSummary {
  emailsDrafted: number
  approvalsProcessed: number
  followupsTracked: number
  hoursSaved: number
}

const IS_MOCK = import.meta.env.VITE_MOCK_API === 'true'

export default function ValueMetricsCard() {
  const { t } = useTranslation('settings')
  const [data, setData] = useState<ValueSummary | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function load() {
      try {
        const summary = IS_MOCK
          ? await mockApi.getActivitySummary('week')
          : await apiRequest<ValueSummary>('/activity/summary?window=week')
        setData(summary)
      } catch {
        // Silently fail — card just won't show
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  if (loading) {
    return (
      <div className="glass-card rounded-xl p-4 mb-6 animate-shimmer">
        <div className="h-4 w-24 bg-charcoal/50 rounded mb-3" />
        <div className="grid grid-cols-2 gap-3">
          <div className="h-12 bg-charcoal/30 rounded-lg" />
          <div className="h-12 bg-charcoal/30 rounded-lg" />
          <div className="h-12 bg-charcoal/30 rounded-lg" />
          <div className="h-12 bg-charcoal/30 rounded-lg" />
        </div>
      </div>
    )
  }

  if (!data) return null

  const total = data.emailsDrafted + data.approvalsProcessed + data.followupsTracked
  if (total === 0) {
    return (
      <div className="glass-card rounded-xl p-4 mb-6">
        <p className="text-pearl/50 text-sm text-center">
          {t('valueMetrics.noActivity')}
        </p>
      </div>
    )
  }

  const stats = [
    { icon: Mail, label: t('valueMetrics.emailsDrafted'), value: data.emailsDrafted, color: 'text-gold' },
    { icon: CheckCircle, label: t('valueMetrics.approvalsProcessed'), value: data.approvalsProcessed, color: 'text-emerald' },
    { icon: TrendingUp, label: t('valueMetrics.followupsTracked'), value: data.followupsTracked, color: 'text-amber' },
    { icon: Clock, label: t('valueMetrics.hoursSaved'), value: data.hoursSaved, color: 'text-gold-light', suffix: 'h' },
  ]

  return (
    <div className="glass-card rounded-xl p-4 mb-6">
      <h3 className="text-xs font-medium text-pearl/50 uppercase tracking-wider mb-3">
        {t('valueMetrics.thisWeek')}
      </h3>
      <div className="grid grid-cols-2 gap-3">
        {stats.map(({ icon: Icon, label, value, color, suffix }) => (
          <div key={label} className="bg-slate-mid/20 rounded-lg p-3 flex items-center gap-2.5">
            <Icon size={16} className={`${color} shrink-0`} />
            <div className="min-w-0">
              <span className={`text-lg font-semibold ${color}`}>
                {value}{suffix}
              </span>
              <p className="text-pearl/50 text-[11px] leading-tight truncate">{label}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
